import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase/client";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { StatusBanner } from "@/components/StatusBanner";
import { friendlyAuthError, GENERIC_ERROR } from "@/lib/errors";
import { ROUTES } from "@/constants/routes";

const FALLBACK = "We couldn't sign you in from this link. If you've already confirmed your email, you can sign in now.";

function readUrlError() {
  const params = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const code = params.get("error_code") || hash.get("error_code");
  const hasError = code || params.get("error") || hash.get("error");
  return hasError ? friendlyAuthError({ code: code || "", message: "" }) : null;
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let active = true;
    const urlError = readUrlError();
    (async () => {
      const { error } = await supabase.auth.initialize();
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      if (data.session) return navigate(ROUTES.dashboard, { replace: true });
      const mapped = urlError || (error ? friendlyAuthError(error) : null);
      setFailure(mapped && mapped !== GENERIC_ERROR ? `${mapped} ${FALLBACK}` : FALLBACK);
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <AuthLayout eyebrow="Email confirmation" title={failure ? "Link not valid." : "Verifying your email."}>
      {failure ? (
        <div data-testid="auth-callback-error" className="space-y-6">
          <StatusBanner>{failure}</StatusBanner>
          <Link to={ROUTES.login} data-testid="auth-callback-login-button" className="mw-btn mw-btn-primary">
            Go to sign in
          </Link>
        </div>
      ) : (
        <div data-testid="auth-callback-loading" className="flex items-center gap-3">
          <span className="mw-spinner" aria-hidden="true" />
          <span className="mw-eyebrow">Verifying your session</span>
        </div>
      )}
    </AuthLayout>
  );
}
